/**
 * Shared formatting utilities for consistent display across the web app
 */

// Map exchange → currency code. Used to give an imported holding the currency
// its listing venue trades in ([[BUGS #262]]) — an ASX line is AUD whether it
// is a share or a fund, which is why nothing here branches on asset type.
// Every value must be in `COST_BASIS_CURRENCIES` below, or a resolved row
// would carry a currency the review table cannot display; a contract test
// pins that.
export const EXCHANGE_CURRENCY_MAP: Record<string, string> = {
  'NYSE': 'USD', 'NASDAQ': 'USD', 'AMEX': 'USD', 'NYSEARCA': 'USD',
  'BATS': 'USD', 'OTC': 'USD', 'PNK': 'USD',
  'ASX': 'AUD',
  'LSE': 'GBP', 'SGX': 'SGD', 'HKSE': 'HKD', 'TSX': 'CAD', 'JPX': 'JPY',
};

/** Exchange → the suffix its listings carry (`ASX` → `.AX`).
 *
 *  Moved here from `pages/PortfolioAnalysis.tsx`, where it was module-private,
 *  because it composes with the map above and a second copy would drift from
 *  it — the failure the parity test beside these exists to prevent. US venues
 *  are absent deliberately: their listings carry no suffix. */
export const EXCHANGE_TO_SUFFIX: Record<string, string> = {
  ASX: '.AX',
  LSE: '.L',
  HKSE: '.HK',
  SGX: '.SI',
  JPX: '.T',
  TSX: '.TO',
};

const SUFFIX_TO_EXCHANGE: Record<string, string> = Object.entries(EXCHANGE_TO_SUFFIX)
  .reduce<Record<string, string>>((acc, [exch, sfx]) => {
    acc[sfx.toUpperCase()] = exch;
    return acc;
  }, {});

/** The venue a symbol's own suffix names (`HUM.AX` → `ASX`), or null.
 *
 *  Null covers both an unsuffixed symbol — which is a US listing, and the
 *  caller decides what that means — and a suffix nothing here knows. */
export function exchangeForSymbolSuffix(symbol?: string | null): string | null {
  if (!symbol) return null;
  const upper = symbol.toUpperCase();
  // Longest-first, so '.AX' beats '.A' if a one-character suffix is ever added.
  const suffixes = Object.keys(SUFFIX_TO_EXCHANGE).sort((a, b) => b.length - a.length);
  for (const sfx of suffixes) {
    if (upper.endsWith(sfx)) return SUFFIX_TO_EXCHANGE[sfx];
  }
  return null;
}

/** The currency a symbol trades in, read from its own suffix.
 *
 *  The two maps above, composed. An unsuffixed symbol is a US listing and
 *  resolves to USD — which is the assumption the intake already makes, stated
 *  here once rather than repeated at every call site. */
export function currencyForSymbol(symbol?: string | null): string | null {
  if (!symbol) return null;
  const exchange = exchangeForSymbolSuffix(symbol);
  return exchange ? currencyForExchange(exchange) : 'USD';
}

/** The currency a risk result's money figures are denominated in.
 *
 *  A book whose holdings all list on one venue is NOT converted — it is
 *  reported in that venue's own currency — while a book spanning several is
 *  converted to USD. So every money figure on a result must be read from
 *  THIS, and reading them all as USD is [[BUGS #289]]: an all-ASX book was
 *  converted a second time and shown at roughly 1.5x its true value.
 *
 *  **USD is the answer only when the field is ABSENT**, which means a report
 *  computed before the currency was recorded. Those must keep rendering
 *  exactly as they do today rather than being retro-converted, so the
 *  fallback is a deliberate statement about history, not a default.
 *
 *  Exported and shared because the screen, the PDF and the spreadsheet each
 *  need the same answer, and three copies of `x || 'USD'` is precisely how
 *  they would come to disagree about one report.
 */
export function reportCurrency(result?: { base_currency?: string } | null): string {
  const named = (result?.base_currency || '').trim();
  return named || 'USD';
}

/** How many distinct currencies a list of symbols spans.
 *
 *  Used to say whether a book's total sums unlike units. Counts what can be
 *  resolved and ignores what cannot — an unrecognised suffix is not evidence
 *  of a second currency, and guessing one would invent the very problem this
 *  reports. */
export function currencySpan(symbols: ReadonlyArray<string>): string[] {
  const seen = new Set<string>();
  for (const symbol of symbols) {
    const currency = currencyForSymbol(symbol);
    if (currency) seen.add(currency);
  }
  return Array.from(seen).sort();
}

/** The symbol a currency is written with (`AUD` -> `A$`), falling back to `$`.
 *
 *  Exported because two surfaces prefix a raw figure with it rather than
 *  formatting through `formatCurrency`, which abbreviates to `$1.5M`. */
export const getCurrencySymbol = (currency: string): string => {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency })
      .formatToParts(0).find(p => p.type === 'currency')?.value || '$';
  } catch { return '$'; }
};

/** The currency a listing on this exchange trades in, or null when unknown.
 *
 *  Shared because two surfaces need the same answer: the portfolio intake,
 *  which must not read an AUD cost basis as USD ([[BUGS #262]]), and the
 *  research price chart, whose axis and headline would otherwise be bare
 *  numbers or, worse, a guessed dollar sign ([[BUGS #273]]). */
export function currencyForExchange(exchange?: string | null): string | null {
  if (!exchange) return null;
  return EXCHANGE_CURRENCY_MAP[exchange.toUpperCase()] || null;
}

export const formatCurrency = (value: number | null | undefined, currency = 'USD'): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return `${getCurrencySymbol(currency)}0`;
  }

  const absValue = Math.abs(value);
  // The sign goes in FRONT of the mark ([[BUGS #364]]): `$-34.8B` reads as a
  // typo, `-$34.8B` as a figure. The magnitude decides the scale.
  const symbol = `${value < 0 ? '-' : ''}${getCurrencySymbol(currency)}`;

  if (absValue >= 1e12) {
    return `${symbol}${(absValue / 1e12).toFixed(1)}T`;
  } else if (absValue >= 1e9) {
    return `${symbol}${(absValue / 1e9).toFixed(1)}B`;
  } else if (absValue >= 1e6) {
    return `${symbol}${(absValue / 1e6).toFixed(1)}M`;
  } else if (absValue >= 1e3) {
    return `${symbol}${(absValue / 1e3).toFixed(1)}K`;
  } else {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  }
};

/** A table cell's money, compact, with the sign where a reader expects it.
 *
 *  [[BUGS #364]]: the screener's *Op. Cash Flow* column read `$93.2B` for a
 *  positive figure and `$-34769891177` for a negative one, because two copies
 *  of this logic (MasterTable, EtfMasterTable) compared the SIGNED value
 *  against the thresholds, so nothing negative ever qualified for a scale.
 *  One copy now, thresholds on the magnitude, sign in front: `-$34.8B`.
 *
 *  Precision matches what those tables always showed — two decimals at
 *  trillions, one at billions and millions, none below. Absence is the
 *  caller's to render; this never sees a null. */
export function formatCompactCurrency(n: number, symbol = '$'): string {
  if (!Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${sign}${symbol}${(abs / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${sign}${symbol}${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}${symbol}${(abs / 1e6).toFixed(1)}M`;
  return `${sign}${symbol}${abs.toFixed(0)}`;
}

/**
 * A percentage a card shows, from a value ALREADY in percent (12.5 → "12.5%").
 *
 * Thirteen local copies of `formatPercent` existed across the app, and they
 * were not the same function: some rendered one decimal and some two, some
 * prefixed a `+` on positives and some did not, and the fallbacks were "N/A",
 * "--" and "—" depending on the file (2026-09-07 quality report). So the
 * options here are not decoration — they are what let every existing call site
 * keep its exact output while there is one implementation behind them.
 *
 * Not to be confused with `formatPercentage` below, which is a DIFFERENT
 * function kept as it is: it guesses whether its input is a fraction or a
 * percentage and falls back to "0.00%", which reads as a real measurement.
 * Reinterpreting a unit is the failure that shipped a 148.7% "share of the
 * book" and a currency-less price axis this week; this one never guesses.
 */
export function formatPercent(
  value: number | null | undefined,
  { decimals = 1, signed = false, fallback = 'N/A' }: {
    decimals?: number; signed?: boolean; fallback?: string;
  } = {},
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  const sign = signed && value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
}

export const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.00%';
  }

  // Handle both decimal (0.25) and percentage (25) formats
  const percentValue = value > 1 ? value : value * 100;
  return `${percentValue.toFixed(2)}%`;
};

export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  return new Intl.NumberFormat('en-US').format(value);
};

/**
 * Supported display currencies for the currency preference feature.
 */
export const SUPPORTED_CURRENCIES = [
  // **"What the backend can CONVERT", not what a user may pick** — the picker
  // is `DISPLAY_CURRENCIES` below, and it is deliberately far shorter.
  //
  // Widened 2026-09-02 to every currency the rate source serves ([[BUGS #239]]).
  // The previous twelve were added one crisis at a time, and the limit bit:
  // measured across 84 symbols, 65% of the universe reports in a non-USD
  // currency, and Telkom Indonesia (IDR) sat outside the list — so the
  // conversion fix would have DROPPED its figures rather than corrected them.
  //
  // Kept in step with `functions/services/fx_rate_service.SUPPORTED_CURRENCIES`,
  // which serves the rates — a contract test pins the two together.
  'USD', 'AUD', 'BRL', 'CAD', 'CHF', 'CNY', 'CZK', 'DKK', 'EUR', 'GBP',
  'HKD', 'HUF', 'IDR', 'ILS', 'INR', 'ISK', 'JPY', 'KRW', 'MXN', 'MYR',
  'NOK', 'NZD', 'PHP', 'PLN', 'RON', 'SEK', 'SGD', 'THB', 'TRY', 'ZAR',
] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

/**
 * The currencies actually OFFERED to a reader — the single list, used by every
 * selector (owner, 2026-08-23: "there should only be one list").
 *
 * `SUPPORTED_CURRENCIES` above is the set the backend can convert and the type
 * is derived from; this is the narrower set we put in front of people. They are
 * different questions, which is how they drifted: the header selector offered
 * six while the risk dashboard offered all eleven, so the same product told a
 * reader two different things about what it supports.
 *
 * Display-only either way — risk analysis always runs in USD, which the
 * dashboard's disclaimer states.
 */
export const DISPLAY_CURRENCIES: SupportedCurrency[] = [
  'USD', 'AUD', 'GBP', 'EUR', 'JPY',
  // CNY added 2026-08-18 so a prospective Chinese investor can read the numbers
  // in the currency they think in.
  'CNY',
];

/**
 * The currencies a COST BASIS may be recorded in — a different question from
 * `DISPLAY_CURRENCIES` above, which is what a reader wants numbers shown in.
 *
 * One list for both surfaces that ask it ([[BUGS #262]]): the screenshot
 * dialog's picker and the review table's per-row select. They had drifted —
 * the dialog offered HKD/SGD/CAD and the table did not, so a row whose
 * exchange resolved to Hong Kong would have held a value its dropdown had no
 * option for and rendered visually blank.
 *
 * Typed as `SupportedCurrency`, so a code the rate service cannot convert is
 * a compile error rather than a silent no-op at analyze time.
 */
export const COST_BASIS_CURRENCIES: { code: SupportedCurrency; label: string }[] = [
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'HKD', label: 'HKD — Hong Kong Dollar' },
  { code: 'SGD', label: 'SGD — Singapore Dollar' },
  { code: 'JPY', label: 'JPY — Japanese Yen' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'EUR', label: 'EUR — Euro' },
  // Carried over from the row select, which offered it before these two lists
  // were merged — merging must not take an option away from someone.
  { code: 'CNY', label: 'CNY — Chinese Yuan' },
];

/**
 * Human-readable currency labels for the preference selector.
 */
export const CURRENCY_LABELS: Record<SupportedCurrency, string> = {
  // Every SUPPORTED currency needs a label even when it is not offered in the
  // selector — the type is derived from that list, and a missing entry is a
  // compile error rather than a silently unlabelled option.
  KRW: 'South Korean Won (KRW)',
  USD: 'US Dollar (USD)',
  AUD: 'Australian Dollar (AUD)',
  GBP: 'British Pound (GBP)',
  EUR: 'Euro (EUR)',
  HKD: 'Hong Kong Dollar (HKD)',
  JPY: 'Japanese Yen (JPY)',
  SGD: 'Singapore Dollar (SGD)',
  CAD: 'Canadian Dollar (CAD)',
  NZD: 'New Zealand Dollar (NZD)',
  CHF: 'Swiss Franc (CHF)',
  CNY: 'Chinese Yuan (CNY)',
  // Reporting currencies, not display options ([[BUGS #239]]). They are here
  // because the type is derived from SUPPORTED_CURRENCIES and a missing entry
  // is a compile error — and because a figure converted out of one may still
  // need naming in a tooltip or an export.
  BRL: 'Brazilian Real (BRL)',
  CZK: 'Czech Koruna (CZK)',
  DKK: 'Danish Krone (DKK)',
  HUF: 'Hungarian Forint (HUF)',
  IDR: 'Indonesian Rupiah (IDR)',
  ILS: 'Israeli Shekel (ILS)',
  INR: 'Indian Rupee (INR)',
  ISK: 'Icelandic Krona (ISK)',
  MXN: 'Mexican Peso (MXN)',
  MYR: 'Malaysian Ringgit (MYR)',
  NOK: 'Norwegian Krone (NOK)',
  PHP: 'Philippine Peso (PHP)',
  PLN: 'Polish Zloty (PLN)',
  RON: 'Romanian Leu (RON)',
  SEK: 'Swedish Krona (SEK)',
  THB: 'Thai Baht (THB)',
  TRY: 'Turkish Lira (TRY)',
  ZAR: 'South African Rand (ZAR)',
};

/**
 * Convert and format a monetary value from one currency to the user's display currency.
 *
 * @param value        The numeric value in the source currency
 * @param fromCurrency ISO currency code of the source (e.g. 'USD')
 * @param toCurrency   ISO currency code to display in (e.g. 'AUD')
 * @param rates        FX rates relative to USD (e.g. { AUD: 1.58, GBP: 0.79 })
 * @returns            Formatted string like "A$1.6B" or "$1.0B"
 */
export const formatConvertedCurrency = (
  value: number | null | undefined,
  fromCurrency: string,
  toCurrency: string,
  rates: Record<string, number>,
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return formatCurrency(0, toCurrency);
  }

  if (fromCurrency === toCurrency) {
    return formatCurrency(value, toCurrency);
  }

  const converted = convertCurrency(value, fromCurrency, toCurrency, rates);
  return formatCurrency(converted, toCurrency);
};

/**
 * Convert a numeric amount between currencies using USD-based rates.
 * Returns the original value if conversion is impossible.
 */
export const convertCurrency = (
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rates: Record<string, number>,
): number => {
  if (fromCurrency === toCurrency) return amount;
  if (!rates || Object.keys(rates).length === 0) return amount;

  // Convert from source → USD → target
  const fromRate = fromCurrency === 'USD' ? 1 : rates[fromCurrency];
  const toRate = toCurrency === 'USD' ? 1 : rates[toCurrency];

  if (!fromRate || !toRate) return amount;

  return (amount / fromRate) * toRate;
};